/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*runt
* windowedComponent.class.ts
* 9-12-2019 - Jelmer Jellema - Spin in het Web B.V.
* Abstracte parentclass van windowed components. Een component is dus
*
* @Component({....}) export class MijnScherm extends WindowedComponent
*
* Dit is een lichte class met wat features voor de interactie met het window waar
* het component verschijnt, en de windowmanager die de windows
*
* Communicatie met het window en de windowmanager
* Als het component in een window wordt afgebeeld, dan zit het een proxy voor het windowobject met
* zijn api in this.myWindow. Dat gebeurt via een inputvariabele en is dus vanaf ngOnInit beschikbaar.
*/

import {ChangeDetectorRef, Directive, HostBinding, inject, Input, Type} from '@angular/core';
import {WindowComponent} from './window/window.component';
import {SihwWindowmanager, WindowCreateOptions} from './windowmanager/windowmanager';

@Directive() //nodig want classes met ng-events of angular decorators moeten gedecoreerd zin
export abstract class WindowedComponent {
  //id-teller, zie id(..)
  static nextId = 0;
  static registeredName: string = `WindowedComponent_${++(WindowedComponent.nextId)}`; //wordt overschreven in register, maar als dat niet gebeurt is het toch uniek
  /**
   * Registreer een subclass als per naam oproepbare WindowedComponent
   * @param name
   */
  static register(this: Type<WindowedComponent>, name: string) {
    try {
      (this as unknown as typeof WindowedComponent).registeredName = name;
    }
    catch(_e)
    {}
    // this.registeredName =  name;
    SihwWindowmanager.register(name, this);
  }

  //zet css class
  @HostBinding('class.windowedcomponent') readonly _hostclass = true;


  private myId = ++(WindowedComponent.nextId);


  protected myWindow: WindowComponent = null;
  //in te vullen properties - in principe readonly

  public _starttitle: string = 'Window'; //in subclass overschrijven. Wordt gebruikt in de standaard title-getter
  public icon: string = ''; //classes - in subclass overschrijven
  public readonly fixedSize: boolean = false; //fixed size, dus niet te wijzigen
  public readonly fixedHeight: number | string = null; //fixed height? Als gezet, wordt dit aanghehouden. Number in pixels, string in style-string
  public readonly fixedWidth: number | string = null; //fixed width? Als gezet, wordt dit aanghehouden. Number in pixels, string in style-string
  public readonly openMaximized: boolean = false; //open altijd gemaximaliseerd
  public readonly closeButton: boolean = true; //tonen we een closebutton? Overschrijfbaar
  protected cdf: ChangeDetectorRef = inject(ChangeDetectorRef);

  // Constructor zonder injector-argument; injector wordt niet meer doorgegeven door subklassen.
  protected constructor() {
    //console.log(`${this.constructor.name} ${this.myId}`);
  }

  ///////////////////// Set de proxy
  @Input('_window') set myWindowSetter(mw: WindowComponent) {
    this.myWindow = mw;
  }


  ////////////////////// ID HELPER ///////////////////////

  /**
   * Maak een uniek id in forms e.d. id('kleur') wordt bijvoorbeeld materieelEdit_3_kleur
   * @param idtag
   */
  public id(idtag): string {
    return `${this.constructor.name}_${this.myId}_${idtag}`.toLowerCase();
  }

  /**
   * Als id, maar zet er een hash voor. Bijvoorbeeld bij tabs of interne links
   * @param idtag
   */
  public hashid(idtag): string {
    return `#${this.id(idtag)}`;
  }


  //////// trackby helper
  public trackByThis(index: number, obj: any) {
    return obj;
  }

  ////////////////////// Window Proxies ///////////////

  /**
   * Geef de huidige title terug. Default is dat starttitle
   * Kan dynamisch overschreven worden
   */
  public get title(): string {
    return this._starttitle;
  }

  /**
   * Zitten we in een actief scherm?
   */
  public get actief(): boolean {
    return !! this.myWindow?.actief;
  }

  /**
   * De css-selector van de container waarin losstaande dropdowns (ng-select appendTo) geplaatst
   * moeten worden. Dat is de body van ons window, zodat zo'n dropdown meeschuift als de gebruiker
   * het window verplaatst. Staan we niet in een window, dan valt het terug op de pagina zelf.
   */
  public get dropdownContainer(): string {
    return this.myWindow?.dropdownContainer ?? 'body';
  }

  /**
   * Open een window - uitgebreid.
   * @param component
   * @param data
   * @param dependent Het window wordt afhankelijk van dit window
   * @param closeDependent Het window sluit als dit window sluit
   */
  public createWindow(component: Type<WindowedComponent> | string, data: any = {}, dependent: boolean = true, closeDependent: boolean = false): Promise<any> {
    if (this.myWindow) {
      let options: WindowCreateOptions = {
        closeDepend: closeDependent
      };
      if (dependent) {
        options.depend = this.myWindow;
      }
      return this.myWindow.createWindow(component, data, options);
    }
  }

  //helpers, voor (click) etc
  dependentWnd(schermnaam: string) {
    return this.createWindow(schermnaam);
  }

  independentWnd(schermnaam: string) {
    return this.createWindow(schermnaam, {}, false);
  }


  /**
   * Open een window als dependend en modal
   * @param component
   * @param data
   */
  public async openModal(component: Type<WindowedComponent> | string, data: any = {}): Promise<any> {
    if (this.myWindow) {
      return this.myWindow.createWindow(component, data, {
        depend: this.myWindow
      }, true);
    } else {
      return Promise.reject();
    }
  }

  public closeWindow(resultaat: any = null, forceer: boolean = false): void {
    if (this.myWindow) {
      void this.myWindow.close(resultaat, forceer);
    }
  }

  ////////////////////////// HOOKS VOOR SUBCLASSES ////////////////
  ////roep super aan!
  /**
   * Hook om de close-actie van het window tegen te houden. Let op: wordt ook aangeroepen via onze closeWindow
   * Wordt alleen aangeroepen als het window de close-actie doet, niet als de windowmanager hard sluit
   * False = weiger
   */
  public async canClose(): Promise<boolean> {
    return true;
  }

  /**
   * Het window gaat dicht (hoe dan ook)
   */
  public closing(): void {

  }

  /**
   * Als een ander window geactiveerd wordt (en ook alleen dan)
   */
  public losingFocus(): void {
    //geen change detection
    this.cdf.detach();
  }

  /**
   * Als dit window geactiveerd wordt door een klik e.d. (niet bij openen)
   */
  public gettingFocus(): void {
    //weer change detection
    this.cdf.reattach();
  }
}
